// Wave 3.99af — Saved Filter Views menu for the Orders list.
// -----------------------------------------------------------------------------
// A single filter-bar control that lets a user:
//   * save the current filter-chip + view-mode combo under a name,
//   * apply any previously saved view in one click,
//   * flag one view as the default that auto-loads when Orders opens,
//   * delete views they no longer need.
//
// Views are PRIVATE to the user and company-scoped on the backend. This
// component owns the list + all CRUD; the parent only supplies the live
// filter/view and an `onApply` callback, plus an optional `onViewsLoaded`
// hook so it can auto-apply the default exactly once on mount.
import React, { useCallback, useEffect, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Bookmark, BookmarkPlus, Star, Trash2, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import {
  listSavedViews, createSavedView, updateSavedView, deleteSavedView,
  summarizeSnapshot, snapshotFromFilter, snapshotMatchesFilter,
} from '@/lib/savedViews';

export function SavedViewsMenu({
  companyId,
  filter,
  view,
  goldsmiths = [],
  customers = [],
  onApply,
  onViewsLoaded,
  hideLabel = false,
}) {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const rows = await listSavedViews(companyId, 'orders');
      setViews(rows);
      if (onViewsLoaded) onViewsLoaded(rows);
    } catch {
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, onViewsLoaded]);

  useEffect(() => { reload(); }, [reload]);

  const currentSnapshot = snapshotFromFilter(filter);

  const applyView = (v) => {
    onApply && onApply(v.snapshot, v.view_mode);
    setPopoverOpen(false);
    toast.success(`Applied “${v.name}”`);
  };

  const toggleDefault = async (v) => {
    setBusyId(v.id);
    try {
      await updateSavedView(companyId, v.id, { is_default: !v.is_default });
      await reload();
      toast.success(v.is_default ? 'Removed default' : `“${v.name}” is now your default`);
    } catch (e) {
      toast.error(extractError(e, 'Could not update view'));
    } finally {
      setBusyId(null);
    }
  };

  const removeView = async (v) => {
    setBusyId(v.id);
    try {
      await deleteSavedView(companyId, v.id);
      await reload();
      toast.success(`Deleted “${v.name}”`);
    } catch (e) {
      toast.error(extractError(e, 'Could not delete view'));
    } finally {
      setBusyId(null);
    }
  };

  const openSaveDialog = () => {
    setName('');
    setMakeDefault(false);
    setPopoverOpen(false);
    setSaveOpen(true);
  };

  const doSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Give this view a name'); return; }
    setSaving(true);
    try {
      await createSavedView(companyId, {
        name: trimmed,
        context: 'orders',
        snapshot: currentSnapshot,
        view_mode: view === 'table' || view === 'grid' ? view : undefined,
        is_default: makeDefault,
      });
      setSaveOpen(false);
      await reload();
      toast.success(`Saved “${trimmed}”`);
    } catch (e) {
      toast.error(extractError(e, 'Could not save view'));
    } finally {
      setSaving(false);
    }
  };

  const count = views.length;

  return (
    <>
      <div>
        {!hideLabel && <Label className="text-[11px] text-slate-500">Views</Label>}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
                count
                  ? 'border-[var(--gold-accent-soft)] bg-[var(--paper-warm)] text-[var(--ink-navy)] hover:bg-[var(--gold-accent-soft)]/40'
                  : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="orders-saved-views-trigger"
              title="Save and reuse filter combinations"
            >
              <Bookmark size={13} className="text-[var(--gold-accent)]" />
              <span>Saved views</span>
              {count > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gold-accent)] text-white text-[10px] font-semibold tabular-nums"
                  data-testid="orders-saved-views-count"
                >
                  {count}
                </span>
              )}
              <ChevronDown size={12} className="opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-80 p-0 overflow-hidden"
            align="end"
            data-testid="orders-saved-views-content"
          >
            <div className="px-3 py-2.5 border-b border-[var(--divider)] flex items-center justify-between">
              <div className="text-sm font-semibold text-[var(--ink-navy)]">Saved views</div>
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Private to you</span>
            </div>

            <div className="max-h-72 overflow-y-auto p-1.5" data-testid="orders-saved-views-list">
              {loading ? (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ) : count === 0 ? (
                <div className="px-3 py-6 text-center" data-testid="orders-saved-views-empty">
                  <Bookmark size={22} className="mx-auto text-slate-300 mb-1.5" />
                  <div className="text-sm text-slate-600">No saved views yet</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Set up your filters, then save them for one-click access.
                  </div>
                </div>
              ) : (
                views.map((v) => {
                  const active = snapshotMatchesFilter(v.snapshot, filter);
                  const busy = busyId === v.id;
                  return (
                    <div
                      key={v.id}
                      className={`group flex items-center gap-1 rounded-md px-1.5 py-1 ${
                        active ? 'bg-[var(--jewellery-bg)]/60' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`orders-saved-view-row-${v.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => applyView(v)}
                        className="flex-1 min-w-0 text-left px-1.5 py-1 rounded"
                        data-testid={`orders-saved-view-apply-${v.id}`}
                        title={`Apply “${v.name}”`}
                      >
                        <div className="flex items-center gap-1.5">
                          {active && <Check size={12} className="text-[var(--jewellery-icon)] shrink-0" />}
                          <span className="text-[13px] font-medium text-[var(--ink-navy)] truncate">
                            {v.name}
                          </span>
                          {v.is_default && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--gold-icon)] bg-[var(--gold-accent-soft)]/50 rounded px-1 py-px shrink-0"
                              data-testid={`orders-saved-view-default-badge-${v.id}`}
                            >
                              <Star size={8} className="fill-current" /> Default
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {summarizeSnapshot(v.snapshot, { goldsmiths, customers })}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleDefault(v)}
                        disabled={busy}
                        className={`shrink-0 p-1.5 rounded transition-colors disabled:opacity-40 ${
                          v.is_default
                            ? 'text-[var(--gold-accent)] hover:bg-[var(--gold-accent-soft)]/40'
                            : 'text-slate-300 hover:text-[var(--gold-accent)] hover:bg-slate-100'
                        }`}
                        aria-label={v.is_default ? 'Remove default' : 'Set as default'}
                        title={v.is_default ? 'Remove as default' : 'Set as default (auto-open)'}
                        data-testid={`orders-saved-view-default-${v.id}`}
                      >
                        <Star size={14} className={v.is_default ? 'fill-current' : ''} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeView(v)}
                        disabled={busy}
                        className="shrink-0 p-1.5 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40"
                        aria-label={`Delete ${v.name}`}
                        title="Delete this view"
                        data-testid={`orders-saved-view-delete-${v.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-[var(--divider)] p-1.5">
              <button
                type="button"
                onClick={openSaveDialog}
                className="w-full inline-flex items-center gap-2 px-2.5 py-2 rounded-md text-[13px] font-medium text-[var(--ink-navy)] hover:bg-[var(--paper-warm)] transition-colors"
                data-testid="orders-saved-views-save-current"
              >
                <BookmarkPlus size={15} className="text-[var(--gold-accent)]" />
                Save current view…
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Save-current-view dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="orders-saved-view-save-dialog">
          <DialogHeader>
            <DialogTitle>Save this view</DialogTitle>
            <DialogDescription>
              Pin the filters you have set right now so you can jump back to them anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="saved-view-name" className="text-[12px] text-slate-600">View name</Label>
              <Input
                id="saved-view-name"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !saving) doSave(); }}
                placeholder="e.g. My urgent diamonds"
                className="h-9 mt-1"
                autoFocus
                data-testid="orders-saved-view-name-input"
              />
            </div>
            <div className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">
                Filters being saved
              </div>
              <div className="text-[12px] text-slate-700" data-testid="orders-saved-view-preview">
                {summarizeSnapshot(currentSnapshot, { goldsmiths, customers })}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                View mode: {view === 'table' ? 'Table' : 'Grid'}
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={makeDefault}
                onCheckedChange={(chk) => setMakeDefault(!!chk)}
                className="mt-0.5"
                data-testid="orders-saved-view-default-toggle"
              />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-800">Make this my default</div>
                <div className="text-[11px] text-slate-500">
                  Auto-open this view every time you land on Orders.
                </div>
              </div>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSaveOpen(false)} data-testid="orders-saved-view-cancel">
              Cancel
            </Button>
            <Button
              className="luxury-button"
              onClick={doSave}
              disabled={saving || !name.trim()}
              data-testid="orders-saved-view-save-confirm"
            >
              {saving ? 'Saving…' : 'Save view'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SavedViewsMenu;
